import React from 'react';

import { useAppSelector } from '@/store';
import {
  fetchArtStyle,
  listArtStyles,
  type ArtStyle,
  type ArtStyleMascot,
  type ArtStyleResult,
  type ArtStyleSummary,
} from './art-style-api';

/** The brand's ACTIVE art style: the one spark adds (its text and its reference images) to every banner and image
 *  the brand generates, with its cast of mascots (empty until the record is read, or when it has none). `mature`
 *  marks an adult-oriented style, which video generation models refuse (so video renders are blocked). */
export type ActiveArtStyle = { id: string; name: string; mascots: ArtStyleMascot[]; mature: boolean };

/** A style name is a hyphenated slug ("vintage-mythic-ink-poster"); show it as words. */
export const artStyleLabel = (name: string): string => {
  const words = name.replace(/[-_]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : '';
};

/* A generate sheet mounts the "Art style" chip and the mascot picker together, and each reads the active style:
 * reads already in flight are shared (per brand, per style) so they cost one request each. Nothing is kept once
 * a read settles, so the next mount reads afresh. */
const listInFlight = new Map<number, Promise<ArtStyleResult<ArtStyleSummary[]>>>();
const recordInFlight = new Map<string, Promise<ArtStyleResult<ArtStyle>>>();

const shared = <K, T>(cache: Map<K, Promise<T>>, key: K, read: () => Promise<T>): Promise<T> => {
  const held = cache.get(key);
  if (held) return held;
  const next = read().finally(() => cache.delete(key));
  cache.set(key, next);
  return next;
};

/** The active style plus whether it is known yet: `loaded` is false until the current brand's style list read has
 *  settled (ok or failed), so a consumer that must fail closed (video renders on a possibly mature style) can tell
 *  "still reading" apart from "no active style". */
/** `failed`: the read settled without an answer (a style may be active): never the same as "no style". */
export type ActiveArtStyleState = { active: ActiveArtStyle | null; loaded: boolean; failed: boolean };

/**
 * The display brand's active art style, or null (none active, no definition yet, or the read failed). Reads the
 * brand's style list and picks the one spark injects: status `active` with a definition (a brand has at most
 * one); then reads that style's record for its cast. Re-read on every mount and brand switch, so a style
 * activated (or a mascot added) on the Art Style tab shows the next time a generate sheet opens.
 */
/** How many times a failed style read is tried again, and how long apart. */
const ART_STYLE_READ_RETRIES = 2;
const ART_STYLE_RETRY_MS = 3000;

export const useActiveArtStyleState = (): ActiveArtStyleState => {
  const displayBrand = useAppSelector((state) => state.casinoInstance.displayBrand);
  const tenantRaw = Number(displayBrand?.tenant_id);
  const tenantId = Number.isFinite(tenantRaw) && tenantRaw > 0 ? tenantRaw : undefined;
  const [active, setActive] = React.useState<ActiveArtStyle | null>(null);
  // The brand whose list read has settled. `loaded` derives from it (not a flag reset in the effect), so the very
  // render after a brand switch already reads as not loaded. A failed read settles too, reported as `failed`
  // (never as "no style").
  const [settledFor, setSettledFor] = React.useState<number | undefined>(undefined);
  // The brand whose list read failed (it settles too: `loaded`, with `failed` set).
  const [failedFor, setFailedFor] = React.useState<number | undefined>(undefined);
  // Which read this is for the brand: a failed one is tried again a few times (a passing network blip heals on its
  // own instead of holding a composer's Generate until it is reopened).
  const [attempt, setAttempt] = React.useState(0);
  React.useEffect(() => setAttempt(0), [tenantId]);

  React.useEffect(() => {
    setActive(null);
    // Cleared too, so a quick switch back to an earlier brand (A to B to A) is not taken as already loaded.
    setSettledFor(undefined);
    setFailedFor(undefined);
    if (!tenantId) return undefined;
    let live = true;
    let retry: number | undefined;
    void shared(listInFlight, tenantId, () => listArtStyles(tenantId)).then((result) => {
      if (!live) return;
      // A service without art-style routes (404, 'unavailable') has no style: that is an answer, not a failure.
      const failed = !result.ok && result.reason !== 'unavailable';
      const style = result.ok
        ? result.value.find((item) => item.status === 'active' && item.has_definition)
        : undefined;
      // The chip shows at once; the cast follows with the record. Set before `settledFor`, so no render sees
      // the brand as loaded without its style.
      if (style) setActive({ id: style.id, name: style.name, mascots: [], mature: style.mature });
      if (failed) {
        setFailedFor(tenantId);
        if (attempt < ART_STYLE_READ_RETRIES) {
          retry = window.setTimeout(() => setAttempt((n) => n + 1), ART_STYLE_RETRY_MS);
        }
      }
      setSettledFor(tenantId);
      if (!style) return;
      void shared(recordInFlight, `${tenantId}:${style.id}`, () => fetchArtStyle(tenantId, style.id)).then(
        (record) => {
          if (!live || !record.ok) return;
          setActive({
            id: style.id,
            name: record.value.name || style.name,
            mascots: record.value.mascots,
            mature: record.value.mature,
          });
        },
      );
    });
    return () => {
      live = false;
      if (retry !== undefined) window.clearTimeout(retry);
    };
  }, [tenantId, attempt]);

  // No brand means nothing to read. While the current brand is unread, hide a previous brand's style too.
  const loaded = !tenantId || settledFor === tenantId;
  return { active: loaded ? active : null, loaded, failed: loaded && tenantId !== undefined && failedFor === tenantId };
};

/** The display brand's active art style, or null (see useActiveArtStyleState for the read). */
export const useActiveArtStyle = (): ActiveArtStyle | null => useActiveArtStyleState().active;
